/* eslint-disable @typescript-eslint/no-explicit-any -- casts formulaire / erreurs axios comme U3206 */
/* eslint-disable react-hooks/exhaustive-deps -- effets alignés chargement id / handlers stables */
import React, { useEffect, useReducer } from 'react';
import type {
  U2000FormSBusinessHandlers,
  U2000FormStateHandlersReturn,
} from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import U2000DefaultForm from '../../u2000Core/components/formControls/U2000DefaultForm';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useU2000GetHandlers } from '../../u2000Core/hooks/handler/U2000UseGetHandlers';
import { u2000IntFormState, useU2000FormStateHandlers } from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useU2000SearchHandlers } from '../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import type { U2000SearchPayload, U2000SearchResult } from '../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GridStateEnumPaginationDirection } from '../../u2000Core/reducer/formReducer/U2000GridState';
import { useU2000SaveHandlers } from '../../u2000Core/hooks/handler/U2000UseSaveHandlers';
import u2000FormState, {
  U2000FormStateEnumEditMode,
  type U2000FormState,
} from '../../u2000Core/reducer/formReducer/U2000FormState';
import { U2000LUStateEnumNextAction } from '../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2000DeleteHandlers } from '../../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import {
  U2000GridStateDeleteAction,
  type U2000GridStateNextRecordAfterDeleteReturn,
} from '../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { NS, U3208ROLES } from '../U3208';
import U3208AiRagCorpusHeader from './form/U3208AiRagCorpusHeader';
import { U3208AiRagCorpusFormToolBar } from './form/U3208AiRagCorpusFormToolBar';
import U3208AiRagCorpusDeleteDlg from './common/U3208AiRagCorpusDeleteDlg';
import U3208SourcesPanel from './form/U3208SourcesPanel';
import { useU3208Context } from '../U3208Context';
import {
  buildCreateBody,
  buildDeleteBody,
  buildUpdateBody,
  createEmptyU3208FormRow,
  doCreateData,
  doDeleteData,
  doGetData,
  doSearch,
  doUpdateData,
  luHandleAppiError,
  normalizeU3208CorpusFromApi,
  u3208IsSystemCorpus,
  type U3208AiRagCorpusFormRow,
  type U3208AiRagCorpusSearchRow,
} from '../U3208Service';
import { useU2000LogicalUnitContext } from '../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import { useU2000AuthContext } from '../../u2000Core/context/auth/U2000UseAuthContext';
import { u2000CanManageScopeAwareEntity } from '../../u2000Core/tools/U2000SystemScopeAccess';

const CODE_PATTERN = /^[A-Z0-9_]+$/;

export interface U3208AiRagCorpusFormExtendedHandlers
  extends U2000FormStateHandlersReturn<U3208AiRagCorpusFormRow> {
  addNewCorpus: () => void;
  modifyCorpus: () => void;
  deleteCorpus: () => Promise<void>;
}

const U3208AiRagCorpusForm: React.FC = () => {
  const { id } = useParams();
  const { auth, hasRole } = useU2000AuthContext();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3208Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const listTitle = t('list.title', { ns: NS });
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const canEditCorpus = (
    row: Pick<U3208AiRagCorpusFormRow, 'id' | 'systemScope' | 'organizationId'> | null | undefined,
    editMode: U2000FormStateEnumEditMode,
  ) =>
    u2000CanManageScopeAwareEntity({
      isSystemScope: row ? u3208IsSystemCorpus(row) : false,
      hasRequiredRole: hasRole(editMode === U2000FormStateEnumEditMode.ADD ? U3208ROLES.ADD : U3208ROLES.EDIT),
      sessionOrganizationId: auth.user?.organizationId,
    });
  const canDeleteCorpus = (
    row: Pick<U3208AiRagCorpusFormRow, 'id' | 'systemScope' | 'organizationId'> | null | undefined,
  ) =>
    u2000CanManageScopeAwareEntity({
      isSystemScope: row ? u3208IsSystemCorpus(row) : false,
      hasRequiredRole: hasRole(U3208ROLES.DELETE),
      sessionOrganizationId: auth.user?.organizationId,
    });

  const validateForm = (row: U3208AiRagCorpusFormRow) => {
    const result: Record<string, string> = {};
    const isAdd = !row.id;

    if (isAdd) {
      const code = row.code?.trim().toUpperCase() ?? '';
      if (!code) {
        result['code'] = t('errCodeRequired', { ns: NS });
      } else if (code.length > 64) {
        result['code'] = t('errCodeMax', { ns: NS });
      } else if (!CODE_PATTERN.test(code)) {
        result['code'] = t('errCodePattern', { ns: NS });
      }
    }

    const title = row.title?.trim() ?? '';
    if (!title) {
      result['title'] = t('errTitleRequired', { ns: NS });
    } else if (title.length > 255) {
      result['title'] = t('errTitleMax', { ns: NS });
    }

    return result;
  };

  const u3208BL: U2000FormSBusinessHandlers<U3208AiRagCorpusFormRow> = {
    newRecord: () => createEmptyU3208FormRow(),
    validateForm,
    saveForme: async (row: U3208AiRagCorpusFormRow, editMode: U2000FormStateEnumEditMode) => {
      if (u3208IsSystemCorpus(row) && editMode !== U2000FormStateEnumEditMode.ADD && !canEditCorpus(row, editMode)) {
        toast.error(t('error.systemImmutable', { ns: NS }), t('errorTitle', { ns: NS }));
        return;
      }
      const errors = validateForm(row);
      if (Object.keys(errors).length > 0) {
        u3208FormExtended.setErrors(errors);
        return;
      }
      handleSave.saveData(row, editMode);
    },
    cancelForm: () => {
      const old = u3208FormExtended.getOldData() as U3208AiRagCorpusFormRow | null;
      if (old) {
        u3208FormExtended.setData({ ...old });
      }
      if (luStateHandlers.getActionOriginFrom() === '/U3208') {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },
    onChangeRecordForm: (row) => {
      if (row.id) {
        handleGet.getData(row.id);
      }
    },
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(
        searchStateHandlers.extractSearchParameter(),
        gridStateHandlers.extractPagination(page),
        direction,
      );
    },
    onLeaveForm: () => {
      navigate('/U3208');
    },
  };

  const u3208FormHandlers = useU2000FormStateHandlers<U3208AiRagCorpusFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: u3208BL,
  });

  const u3208FormExtended: U3208AiRagCorpusFormExtendedHandlers = {
    ...u3208FormHandlers,
    addNewCorpus: () => {
      u3208FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
    },
    modifyCorpus: () => {
      if (!canEditCorpus(u3208FormExtended.getData(), U2000FormStateEnumEditMode.EDIT)) return;
      u3208FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteCorpus: async () => {
      if (!canDeleteCorpus(u3208FormExtended.getData())) return;
      await handleDelete.handleDelete();
    },
  };

  const handleSearch = useU2000SearchHandlers<U3208AiRagCorpusSearchRow>({
    doSearchFn: (searchPayload: U2000SearchPayload) => doSearch(u2000AxiosAuthenticated, searchPayload),
    onSuccess: (searchResult: U2000SearchResult<U3208AiRagCorpusSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleGet = useU2000GetHandlers<U3208AiRagCorpusFormRow>({
    doGetFn: (rowId: string) => doGetData(u2000AxiosAuthenticated, rowId),
    onSuccess: (row: U3208AiRagCorpusFormRow) => {
      const normalized = normalizeU3208CorpusFromApi(row);
      gridStateHandlers.setDataById(normalized.id, normalized as unknown as U3208AiRagCorpusSearchRow);
      u3208FormExtended.setData(normalized);

      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
      navigate(newPath, { replace: true });

      const crumbLabel = `${normalized.code}${normalized.title ? ` — ${normalized.title}` : ''}`;
      setLogicalUnitBreadcrumb([
        { href: '/U3208', label: listTitle },
        { label: crumbLabel },
      ]);

      if (u3208IsSystemCorpus(normalized) && !canEditCorpus(normalized, U2000FormStateEnumEditMode.EDIT)) {
        u3208FormExtended.setEditMode(U2000FormStateEnumEditMode.NONE);
      } else if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3208FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU2000SaveHandlers<U3208AiRagCorpusFormRow>({
    doSaveFn: (row: U3208AiRagCorpusFormRow, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, buildCreateBody(row));
      }
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, row.id, buildUpdateBody(row));
      }
      return Promise.resolve(undefined);
    },
    onSuccess: (row: U3208AiRagCorpusFormRow, editMode: U2000FormStateEnumEditMode) => {
      const normalized = normalizeU3208CorpusFromApi(row);
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), normalized as unknown as U3208AiRagCorpusSearchRow]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length);
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(row.id, normalized as unknown as U3208AiRagCorpusSearchRow);
      }
      u3208FormExtended.afterSaveData(normalized);
      u3208FormExtended.setData(normalized);
      toast.success(t('msgLogicalUnitSaved', { ns: NS }));

      if (
        luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD ||
        luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY
      ) {
        if (luStateHandlers.getActionOriginFrom() === '/U3208') {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3208FormExtended.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3208FormExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => {
      const data = u3208FormExtended.getData();
      return data ? [data as unknown as U3208AiRagCorpusSearchRow] : [];
    },
    doDeleteFn: async (rowId) => {
      const data = u3208FormExtended.getData();
      if (!data) return;
      return await doDeleteData(u2000AxiosAuthenticated, rowId, buildDeleteBody(data));
    },
    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((del) => {
        toast.success(
          t('success0003', { key: (del as U3208AiRagCorpusSearchRow).code, ns: NS }),
          t('successTitle', { ns: NS }),
        );
        newData = newData.filter((dataRow) => dataRow.id !== del?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(
        currentIdx,
        newData,
        totalRows,
      );
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.PREVIOUSPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate('/U3208', { replace: true });
      }
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  useEffect(() => {
    if (id === undefined) return;
    if (id === 'new') {
      u3208FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
      u3208FormExtended.setData(createEmptyU3208FormRow());
      setLogicalUnitBreadcrumb([
        { href: '/U3208', label: listTitle },
        { label: t('lblNewCorpus', { ns: NS }) },
      ]);
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  if (u3208FormExtended.getData() === null) {
    return null;
  }

  const data = u3208FormExtended.getData();
  const isSystem = data ? u3208IsSystemCorpus(data) : false;
  const sourcesReadOnly = data ? !canEditCorpus(data, U2000FormStateEnumEditMode.EDIT) : isSystem;

  return (
    <>
      <U3208AiRagCorpusDeleteDlg handleDelete={handleDelete as any} t={t} NS={NS} />

      <U2000DefaultForm
        actionsBtns={<U3208AiRagCorpusFormToolBar formStateHandlers={u3208FormExtended as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3208FormExtended as any}
      >
        <U3208AiRagCorpusHeader formStateHandlers={u3208FormExtended as any} t={t} />
        <U3208SourcesPanel corpusId={data?.id || id} readOnly={sourcesReadOnly} />
      </U2000DefaultForm>
    </>
  );
};

export default U3208AiRagCorpusForm;
